/* ==========================================================================\n   Theme UI Bundle\n   Consolidated global UI shell scripts loaded from layout/theme.liquid.\n   Includes header, navigation drawers, localization drawer, and field state.\n   ========================================================================== */



/* --- Migrated from assets/drawer-menu.js --- */

/**
 * Theme header drawer menu controller.
 *
 * Premium pattern notes:
 * - Prestige / Impact: dialog-style drawer state, aria sync, Escape + outside close, scroll lock
 * - Symmetry: delegated listeners and one mobile nav controller handling nested toggles
 * - Wonder: drawer-level focus containment + body overlay lock while active
 * - Theme adopts the same reliability model with a lightweight, section-safe controller
 */
(() => {
  const SECTION_SELECTOR = '.header[data-section-type="header"]';
  const LOCK_CLASS = 'theme-drawer-menu-lock';
  const LOCK_ATTR = 'data-theme-drawer-menu-lock-count';
  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const SCROLLBAR_VAR = '--theme-scrollbar-compensation';
  const SUBMENU_ANIMATION_FALLBACK = 180;
  const DESKTOP_MEDIA = '(min-width: 1000px)';
  const DEFAULT_CLOSE_DURATION = 360;
  const DEFAULT_TAB_TRANSITION_MS = 320;
  const TABS_ENHANCED_CLASS = 'is-tabs-enhanced';
  const TAB_ENTER_FROM_CLASS = 'is-tab-entering';
  const TAB_ENTER_ACTIVE_CLASS = 'is-tab-enter-active';
  const DRAWER_STATE_EVENT = 'theme:drawer-menu:state';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const controllers = new Map();

  const parseDurationMs = (rawValue) => {
    if (!rawValue) return NaN;
    const value = rawValue.trim();

    if (value.endsWith('ms')) {
      return Number.parseFloat(value);
    }

    if (value.endsWith('s')) {
      return Number.parseFloat(value) * 1000;
    }

    return Number.parseFloat(value);
  };

  const getMotionDuration = (element) => {
    const style = window.getComputedStyle(element || document.documentElement);
    const duration = parseDurationMs(style.getPropertyValue('--motion-duration-normal'));
    if (Number.isFinite(duration) && duration > 0) {
      return duration;
    }

    return DEFAULT_CLOSE_DURATION;
  };

  const getSubmenuDuration = (element) => {
    const style = window.getComputedStyle(element || document.documentElement);
    const fast = parseDurationMs(style.getPropertyValue('--motion-duration-fast'));
    if (Number.isFinite(fast) && fast > 0) {
      return fast;
    }

    const normal = parseDurationMs(style.getPropertyValue('--motion-duration-normal'));
    if (Number.isFinite(normal) && normal > 0) {
      return Math.max(120, Math.min(normal, 260));
    }

    return SUBMENU_ANIMATION_FALLBACK;
  };

  const getSectionKey = (sectionRoot) => sectionRoot.getAttribute('data-section-id') || sectionRoot.id || '';

  const dispatchDrawerState = ({ open, sectionRoot, drawerId }) => {
    document.dispatchEvent(
      new CustomEvent(DRAWER_STATE_EVENT, {
        detail: {
          open: Boolean(open),
          sectionId: getSectionKey(sectionRoot),
          drawerId: drawerId || '',
        },
      })
    );
  };

  const getSectionRootFromNode = (node) => {
    if (!node) return null;
    if (node.matches && node.matches(SECTION_SELECTOR)) return node;
    if (node.querySelector) return node.querySelector(SECTION_SELECTOR);
    return null;
  };

  const getScrollbarCompensation = () => Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const acquireGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty(SCROLLBAR_VAR, compensation);
      body.style.setProperty(SCROLLBAR_VAR, compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty(SCROLLBAR_VAR);
      body.style.removeProperty(SCROLLBAR_VAR);
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  const lockScroll = () => {
    const current = Number.parseInt(document.documentElement.getAttribute(LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      document.documentElement.classList.add(LOCK_CLASS);
      document.body.classList.add(LOCK_CLASS);
      acquireGlobalScrollLock();
    }

    document.documentElement.setAttribute(LOCK_ATTR, String(current + 1));
  };

  const unlockScroll = () => {
    const current = Number.parseInt(document.documentElement.getAttribute(LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      document.documentElement.classList.remove(LOCK_CLASS);
      document.body.classList.remove(LOCK_CLASS);
      document.documentElement.removeAttribute(LOCK_ATTR);
      releaseGlobalScrollLock();
      return;
    }

    document.documentElement.setAttribute(LOCK_ATTR, String(next));
  };

  class DrawerMenuController {
    constructor(sectionRoot) {
      this.sectionRoot = sectionRoot;
      this.sectionId = sectionRoot.getAttribute('data-section-id') || '';
      this.drawerId = this.sectionId ? `HeaderDrawerMenu-${this.sectionId}` : '';
      this.drawer = null;
      this.panel = null;
      this.closeButton = null;
      this.overlay = null;

      this.activeTab = 'shop';
      this.isOpen = false;
      this.opener = null;
      this.desktopQuery = window.matchMedia(DESKTOP_MEDIA);
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileExploreEnabled = false;
      this.submenuHideTimers = new WeakMap();
      this.submenuAnimationMs = SUBMENU_ANIMATION_FALLBACK;
      this.closeTimer = 0;
      this.closeAnimationMs = DEFAULT_CLOSE_DURATION;
      this.openStartRafId = 0;
      this.openCommitRafId = 0;
      this.openClassCommitTimer = 0;
      this.listRevealStartRafId = 0;
      this.listRevealCommitRafId = 0;
      this.listRevealCleanupTimer = 0;
      this.tabEnterCleanupTimer = 0;

      this.boundOnTriggerClick = this.onTriggerClick.bind(this);
      this.boundOnDrawerClick = this.onDrawerClick.bind(this);
      this.boundOnDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.boundOnDocumentFocusIn = this.onDocumentFocusIn.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnOverlayClick = this.onOverlayClick.bind(this);

      this.triggers = Array.from(this.sectionRoot.querySelectorAll('[data-drawer-menu-toggle]'));
    }

    init() {
      if (this.drawerId) {
        this.drawer = document.getElementById(this.drawerId);
      }

      if (!this.drawer) {
        const explicitControlId = this.triggers
          .map((trigger) => trigger.getAttribute('aria-controls'))
          .find((controlsId) => Boolean(controlsId));

        if (explicitControlId) {
          this.drawerId = explicitControlId;
          this.drawer = document.getElementById(explicitControlId);
        }
      }

      if (!this.drawer) {
        this.drawer = this.sectionRoot.querySelector('[data-drawer-menu]');
      }

      if (!this.drawer) return false;

      this.drawerId = this.drawer.id || this.drawerId;
      this.panel = this.drawer.querySelector('[data-drawer-menu-panel]');
      this.closeButton = this.drawer.querySelector('[data-drawer-menu-close]');
      this.overlay = this.drawer.querySelector('[data-drawer-menu-overlay]');
      this.mobileExploreEnabled = this.drawer.dataset.mobileExplore === 'true';
      this.drawer.classList.add(TABS_ENHANCED_CLASS);

      if (!this.panel) return false;

      this.triggers = this.triggers.filter((trigger) => {
        const controlsId = trigger.getAttribute('aria-controls');
        return !controlsId || controlsId === this.drawer.id;
      });

      if (!this.triggers.length && this.drawer.id) {
        this.triggers = Array.from(document.querySelectorAll('[data-drawer-menu-toggle]')).filter((trigger) => {
          return trigger.getAttribute('aria-controls') === this.drawer.id;
        });
      }

      if (!this.triggers.length) return false;

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.boundOnTriggerClick);
      });

      this.drawer.addEventListener('click', this.boundOnDrawerClick);
      this.overlay?.addEventListener('click', this.boundOnOverlayClick);
      if (typeof this.desktopQuery.addEventListener === 'function') {
        this.desktopQuery.addEventListener('change', this.boundOnResize);
      } else if (typeof this.desktopQuery.addListener === 'function') {
        this.desktopQuery.addListener(this.boundOnResize);
      }
      window.addEventListener('resize', this.boundOnResize, { passive: true });

      this.syncTriggerState(false);
      this.setActiveTab('shop', { animate: false });
      this.resetSubmenus(this.drawer);
      this.drawer.hidden = true;
      this.drawer.setAttribute('aria-hidden', 'true');
      this.closeAnimationMs = getMotionDuration(this.drawer);
      this.submenuAnimationMs = getSubmenuDuration(this.drawer);

      return true;
    }

    destroy() {
      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.boundOnTriggerClick);
      });

      this.drawer?.removeEventListener('click', this.boundOnDrawerClick);
      this.overlay?.removeEventListener('click', this.boundOnOverlayClick);
      if (typeof this.desktopQuery.removeEventListener === 'function') {
        this.desktopQuery.removeEventListener('change', this.boundOnResize);
      } else if (typeof this.desktopQuery.removeListener === 'function') {
        this.desktopQuery.removeListener(this.boundOnResize);
      }
      window.removeEventListener('resize', this.boundOnResize);
      window.clearTimeout(this.closeTimer);
      this.cancelPendingOpen();
      this.cancelPendingListReveal();
      window.clearTimeout(this.tabEnterCleanupTimer);
      this.tabEnterCleanupTimer = 0;
      this.drawer?.querySelectorAll('[data-drawer-submenu-panel]').forEach((panel) => {
        this.cancelSubmenuHideTimer(panel);
      });
      this.getTabPanels().forEach((panel) => this.clearTabPanelEnterState(panel));
      this.drawer?.classList.remove(TABS_ENHANCED_CLASS);

      if (this.isOpen) {
        this.teardownOpenState({ returnFocus: false, skipAnimation: true });
      }
    }

    onTriggerClick(event) {
      event.preventDefault();
      const trigger = event.currentTarget;

      if (this.isOpen) {
        this.close({ returnFocus: true });
      } else {
        this.open(trigger);
      }
    }

    onOverlayClick(event) {
      event.preventDefault();
      this.close({ returnFocus: true });
    }

    onDrawerClick(event) {
      const closeButton = event.target.closest('[data-drawer-menu-close]');
      if (closeButton) {
        event.preventDefault();
        this.close({ returnFocus: true });
        return;
      }

      const tabButton = event.target.closest('[data-drawer-menu-tab]');
      if (tabButton) {
        event.preventDefault();
        const tab = tabButton.getAttribute('data-tab-target');
        this.setActiveTab(tab, { focusTab: false });
        this.resetSubmenus(this.drawer);
        return;
      }

      const submenuBack = event.target.closest('[data-drawer-submenu-back]');
      if (submenuBack) {
        event.preventDefault();
        this.closeSubmenuByBack(submenuBack);
        return;
      }

      const submenuToggle = event.target.closest('[data-drawer-submenu-toggle]');
      if (submenuToggle) {
        event.preventDefault();
        this.toggleSubmenu(submenuToggle);
      }
    }

    onDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ returnFocus: true });
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.focusElement(this.panel);
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        this.focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        this.focusElement(first);
      }
    }

    onDocumentFocusIn(event) {
      if (!this.isOpen) return;
      if (this.panel.contains(event.target)) return;

      const focusable = this.getFocusableElements();
      if (focusable.length) {
        this.focusElement(focusable[0]);
      } else {
        this.focusElement(this.panel);
      }
    }

    onResize() {
      if (!this.isOpen) return;

      if (!this.mobileExploreEnabled && !this.isDesktop() && this.activeTab === 'explore') {
        this.setActiveTab('shop', { animate: false });
      }
    }

    isDesktop() {
      return this.desktopQuery.matches;
    }

    focusElement(element) {
      if (!(element instanceof HTMLElement)) return;
      if (typeof element.focus !== 'function') return;

      try {
        element.focus({ preventScroll: true });
      } catch (_error) {
        element.focus();
      }
    }

    prefersReducedMotion() {
      return this.reducedMotionQuery.matches;
    }

    getTabPanelTransitionMs() {
      if (!this.drawer) return DEFAULT_TAB_TRANSITION_MS;
      const style = window.getComputedStyle(this.drawer);
      const explicitDuration = parseDurationMs(style.getPropertyValue('--tab-panel-transition-duration'));
      if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
        return Math.max(120, Math.min(explicitDuration, 700));
      }

      const fallbackDuration = parseDurationMs(style.getPropertyValue('--motion-duration-normal'));
      if (Number.isFinite(fallbackDuration) && fallbackDuration > 0) {
        return Math.max(120, Math.min(fallbackDuration, 700));
      }

      return DEFAULT_TAB_TRANSITION_MS;
    }

    clearTabPanelEnterState(panel) {
      if (!(panel instanceof HTMLElement)) return;
      panel.classList.remove(TAB_ENTER_FROM_CLASS, TAB_ENTER_ACTIVE_CLASS);
    }

    open(trigger) {
      if (this.isOpen || !this.drawer) return;

      this.isOpen = true;
      this.opener = trigger || document.activeElement;

      this.setActiveTab('shop', { animate: false });
      this.resetSubmenus(this.drawer);
      this.syncTriggerState(true);

      // Cancel any in-progress close animation before re-opening.
      // Track whether we're interrupting a close so we don't double-lock scroll.
      window.clearTimeout(this.closeTimer);
      const wasClosing = this.drawer.classList.contains('is-closing');
      this.cancelPendingOpen();
      this.drawer.classList.remove('is-closing', 'is-opening', 'is-open');

      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      if (!wasClosing) {
        lockScroll();
      }
      this.drawer.classList.add('is-opening');

      document.addEventListener('keydown', this.boundOnDocumentKeydown);
      document.addEventListener('focusin', this.boundOnDocumentFocusIn);

      if (this.panel) void this.panel.offsetHeight;
      void this.drawer.offsetHeight;
      this.drawer.classList.remove('is-opening', 'is-closing');
      this.drawer.classList.add('is-open');
      this.scheduleActivePanelListReveal();
      dispatchDrawerState({ open: true, sectionRoot: this.sectionRoot, drawerId: this.drawer.id });

      const initialFocus = this.closeButton || this.getFocusableElements()[0] || this.panel;
      this.focusElement(initialFocus);
    }

    close({ returnFocus = true } = {}) {
      if (!this.isOpen || !this.drawer) return;
      this.teardownOpenState({ returnFocus, skipAnimation: false });
    }

    teardownOpenState({ returnFocus = true, skipAnimation = false } = {}) {
      if (!this.drawer) return;

      this.isOpen = false;
      this.syncTriggerState(false);
      this.cancelPendingOpen();
      this.cancelPendingListReveal();
      this.getTabPanels().forEach((panel) => panel.classList.remove('is-list-reveal-active'));

      document.removeEventListener('keydown', this.boundOnDocumentKeydown);
      document.removeEventListener('focusin', this.boundOnDocumentFocusIn);

      const finishClose = () => {
        this.drawer.classList.remove('is-closing', 'is-opening');
        this.drawer.hidden = true;
        this.drawer.setAttribute('aria-hidden', 'true');
        unlockScroll();
        dispatchDrawerState({ open: false, sectionRoot: this.sectionRoot, drawerId: this.drawer.id });
      };

      if (skipAnimation) {
        window.clearTimeout(this.closeTimer);
        this.drawer.classList.remove('is-open', 'is-closing', 'is-opening');
        finishClose();
      } else {
        if (this.closeAnimationMs <= 0) {
          this.drawer.classList.remove('is-open', 'is-closing', 'is-opening');
          finishClose();
          if (returnFocus && this.opener && typeof this.opener.focus === 'function') {
            this.focusElement(this.opener);
          }
          this.opener = null;
          return;
        }
        // Stage the exit: is-closing keeps visibility:visible while is-open
        // removal triggers the CSS clip-path / opacity / backdrop exit transitions.
        this.drawer.classList.add('is-closing');
        this.drawer.classList.remove('is-opening');
        this.drawer.classList.remove('is-open');
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(finishClose, this.closeAnimationMs);
      }

      if (returnFocus && this.opener && typeof this.opener.focus === 'function') {
        this.focusElement(this.opener);
      }

      this.opener = null;
    }

    cancelPendingOpen() {
      if (this.openStartRafId) {
        window.cancelAnimationFrame(this.openStartRafId);
        this.openStartRafId = 0;
      }
      if (this.openCommitRafId) {
        window.cancelAnimationFrame(this.openCommitRafId);
        this.openCommitRafId = 0;
      }
      if (this.openClassCommitTimer) {
        window.clearTimeout(this.openClassCommitTimer);
        this.openClassCommitTimer = 0;
      }
    }

    cancelPendingListReveal() {
      if (this.listRevealStartRafId) {
        window.cancelAnimationFrame(this.listRevealStartRafId);
        this.listRevealStartRafId = 0;
      }

      if (this.listRevealCommitRafId) {
        window.cancelAnimationFrame(this.listRevealCommitRafId);
        this.listRevealCommitRafId = 0;
      }

      if (this.listRevealCleanupTimer) {
        window.clearTimeout(this.listRevealCleanupTimer);
        this.listRevealCleanupTimer = 0;
      }
    }

    syncTriggerState(isExpanded) {
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      });
    }

    getFocusableElements() {
      if (!this.panel) return [];
      return Array.from(this.panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.hasAttribute('disabled')) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        if (element.closest('[hidden]')) return false;
        if (element.offsetParent === null && getComputedStyle(element).position !== 'fixed') return false;
        return true;
      });
    }

    getTabButtons() {
      return Array.from(this.drawer.querySelectorAll('[data-drawer-menu-tab]'));
    }

    getTabPanels() {
      return Array.from(this.drawer.querySelectorAll('[data-drawer-menu-tab-panel]'));
    }

    getActivePanel() {
      return this.drawer.querySelector(`[data-drawer-menu-tab-panel="${this.activeTab}"]`);
    }

    scheduleActivePanelListReveal() {
      const activePanel = this.getActivePanel();
      this.schedulePanelListReveal(activePanel);
    }

    schedulePanelListReveal(panel) {
      if (!(panel instanceof HTMLElement)) return;

      panel.classList.remove('is-list-reveal-active');

      const topLevelItems = Array.from(panel.querySelectorAll('.drawer-menu__list--level-1 > .drawer-menu__item'));
      if (!topLevelItems.length || this.prefersReducedMotion()) return;

      topLevelItems.forEach((item, index) => {
        const staggerIndex = Math.min(index, 12);
        item.style.setProperty('--drawer-menu-item-stagger-index', String(staggerIndex));
      });

      this.cancelPendingListReveal();

      this.listRevealStartRafId = window.requestAnimationFrame(() => {
        this.listRevealStartRafId = 0;

        this.listRevealCommitRafId = window.requestAnimationFrame(() => {
          this.listRevealCommitRafId = 0;
          if (!this.isOpen || panel.hidden || !this.drawer?.contains(panel)) return;
          panel.classList.add('is-list-reveal-active');

          // Remove class after stagger animations complete so that the
          // animation's fill-forward transform doesn't create a containing
          // block that breaks subpanel absolute positioning.
          const maxStagger = Math.min(topLevelItems.length - 1, 12) * 36;
          this.listRevealCleanupTimer = window.setTimeout(() => {
            this.listRevealCleanupTimer = 0;
            panel.classList.remove('is-list-reveal-active');
          }, 420 + maxStagger + 80);
        });
      });
    }

    setActiveTab(tab, { focusTab = false, animate = true } = {}) {
      const tabButtons = this.getTabButtons();
      const tabPanels = this.getTabPanels();
      if (!tabButtons.length || !tabPanels.length) return;

      const previousTab = this.activeTab;
      let nextTab = tab || 'shop';
      const nextButton = tabButtons.find((button) => button.getAttribute('data-tab-target') === nextTab);
      const nextPanel = tabPanels.find((panel) => panel.getAttribute('data-drawer-menu-tab-panel') === nextTab);

      if (!nextButton || !nextPanel) {
        nextTab = 'shop';
      }

      if (!this.mobileExploreEnabled && !this.isDesktop() && nextTab === 'explore') {
        nextTab = 'shop';
      }

      this.activeTab = nextTab;
      const activePanel =
        tabPanels.find((panel) => panel.getAttribute('data-drawer-menu-tab-panel') === this.activeTab) || null;
      const previousPanel =
        tabPanels.find((panel) => panel.getAttribute('data-drawer-menu-tab-panel') === previousTab) || null;
      const shouldAnimate = Boolean(
        animate &&
          !this.prefersReducedMotion() &&
          this.isOpen &&
          this.drawer?.classList.contains('is-open') &&
          activePanel &&
          previousPanel &&
          activePanel !== previousPanel
      );

      window.clearTimeout(this.tabEnterCleanupTimer);
      this.tabEnterCleanupTimer = 0;

      tabButtons.forEach((button) => {
        const isActive = button.getAttribute('data-tab-target') === this.activeTab;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });

      tabPanels.forEach((panel) => {
        const isActive = panel.getAttribute('data-drawer-menu-tab-panel') === this.activeTab;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
        if (!isActive) {
          this.clearTabPanelEnterState(panel);
        }
      });

      if (shouldAnimate && activePanel) {
        activePanel.classList.add(TAB_ENTER_FROM_CLASS);
        void activePanel.offsetHeight;
        activePanel.classList.remove(TAB_ENTER_FROM_CLASS);
        activePanel.classList.add(TAB_ENTER_ACTIVE_CLASS);
        this.tabEnterCleanupTimer = window.setTimeout(() => {
          this.clearTabPanelEnterState(activePanel);
          this.tabEnterCleanupTimer = 0;
        }, this.getTabPanelTransitionMs() + 40);
      } else if (activePanel) {
        this.clearTabPanelEnterState(activePanel);
      }

      if (this.isOpen && this.drawer?.classList.contains('is-open')) {
        this.scheduleActivePanelListReveal();
      }

      if (focusTab) {
        const activeButton = tabButtons.find((button) => button.getAttribute('data-tab-target') === this.activeTab);
        this.focusElement(activeButton);
      }
    }

    resetSubmenus(scope) {
      if (!scope) return;

      const toggles = scope.querySelectorAll('[data-drawer-submenu-toggle]');
      toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));

      const items = scope.querySelectorAll('[data-drawer-menu-item].is-open');
      items.forEach((item) => item.classList.remove('is-open'));

      const panels = scope.querySelectorAll('[data-drawer-submenu-panel]');
      panels.forEach((panel) => {
        this.cancelSubmenuHideTimer(panel);
        panel.classList.remove('is-active');
        panel.hidden = true;
      });
    }

    toggleSubmenu(toggle) {
      const panelId = toggle.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      const item = toggle.closest('[data-drawer-menu-item]');
      const parentList = item?.parentElement;

      if (!panel || !item || !parentList) return;

      const isOpen = item.classList.contains('is-open');

      const siblingItems = Array.from(parentList.querySelectorAll(':scope > .drawer-menu__item.drawer-menu__item--has-children'));
      siblingItems.forEach((siblingItem) => {
        if (siblingItem === item) return;
        const siblingToggle = siblingItem.querySelector('[data-drawer-submenu-toggle][aria-controls]');
        if (!siblingToggle) return;
        this.closeSubmenuByToggle(siblingToggle, { immediate: true });
      });

      if (isOpen) {
        this.closeSubmenuByToggle(toggle, { immediate: false });
      } else {
        this.openSubmenuByToggle(toggle);
      }
    }

    openSubmenuByToggle(toggle) {
      const panelId = toggle.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      const item = toggle.closest('[data-drawer-menu-item]');
      if (!panel || !item) return;

      // Cancel any running list-reveal stagger animation so that the
      // transform on animated items doesn't create a containing block
      // that traps the subpanel's absolute positioning to the item.
      const tabPanel = item.closest('[data-drawer-menu-tab-panel]');
      if (tabPanel) {
        tabPanel.classList.remove('is-list-reveal-active');
        this.cancelPendingListReveal();
      }

      this.cancelSubmenuHideTimer(panel);
      this.setSubmenuExpanded(panelId, true);
      item.classList.add('is-open');
      panel.hidden = false;

      // Force layout so the browser commits the base state (translateX(100%),
      // opacity 0, visibility hidden) before is-active triggers transitions.
      void panel.offsetHeight;
      panel.classList.add('is-active');

      const focusTarget = panel.querySelector('[data-drawer-submenu-back]') || panel.querySelector('a[href]');
      if (focusTarget instanceof HTMLElement) {
        this.focusElement(focusTarget);
      }
    }

    closeSubmenuByToggle(toggle, { immediate = false } = {}) {
      const panelId = toggle.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      const item = toggle.closest('[data-drawer-menu-item]');
      if (!panel || !item) return;

      this.closeSubmenuPanel(panel, { immediate, focusParent: false });
    }

    closeSubmenuByBack(backButton) {
      const panel = backButton.closest('[data-drawer-submenu-panel]');
      if (!panel) return;
      this.closeSubmenuPanel(panel, { immediate: false, focusParent: true });
    }

    closeSubmenuPanel(panel, { immediate = false, focusParent = false } = {}) {
      if (!panel) return;

      const toggleId = panel.getAttribute('aria-labelledby');
      const toggle = toggleId ? document.getElementById(toggleId) : null;
      const item = toggle?.closest('[data-drawer-menu-item]');
      const panelId = panel.id;

      this.setSubmenuExpanded(panelId, false);
      item?.classList.remove('is-open');

      this.resetSubmenus(panel);
      this.cancelSubmenuHideTimer(panel);
      panel.classList.remove('is-active');

      const shouldHideImmediately = immediate || this.prefersReducedMotion();
      if (shouldHideImmediately) {
        panel.hidden = true;
      } else {
        const duration = this.getSubmenuAnimationMs(panel);
        const timeoutId = window.setTimeout(() => {
          panel.hidden = true;
          this.submenuHideTimers.delete(panel);
        }, duration);
        this.submenuHideTimers.set(panel, { timeoutId });
      }

      if (focusParent && toggle instanceof HTMLElement) {
        toggle.focus({ preventScroll: true });
      }
    }

    setSubmenuExpanded(panelId, isExpanded) {
      if (!panelId || !this.drawer) return;

      const toggles = Array.from(this.drawer.querySelectorAll('[data-drawer-submenu-toggle]')).filter((toggle) => {
        return toggle.getAttribute('aria-controls') === panelId;
      });

      toggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      });
    }

    cancelSubmenuHideTimer(panel) {
      if (!panel) return;
      const hideState = this.submenuHideTimers.get(panel);
      if (!hideState) return;
      if (hideState.timeoutId) {
        window.clearTimeout(hideState.timeoutId);
      }
      this.submenuHideTimers.delete(panel);
    }

    getSubmenuAnimationMs(panel) {
      const style = window.getComputedStyle(panel);
      const propertyDuration = (style.transitionDuration || '')
        .split(',')
        .map((value) => parseDurationMs(value))
        .find((value) => Number.isFinite(value) && value > 0);

      if (Number.isFinite(propertyDuration) && propertyDuration > 0) {
        return propertyDuration;
      }

      return this.submenuAnimationMs || SUBMENU_ANIMATION_FALLBACK;
    }
  }

  const mountSection = (sectionRoot) => {
    if (!sectionRoot) return;
    const key = getSectionKey(sectionRoot);
    if (!key) return;

    const existingController = controllers.get(key);
    if (existingController) {
      existingController.destroy();
    }

    const controller = new DrawerMenuController(sectionRoot);
    if (!controller.init()) return;

    controllers.set(key, controller);
  };

  const unmountSection = (sectionRoot) => {
    if (!sectionRoot) return;
    const key = getSectionKey(sectionRoot);
    if (!key) return;

    const controller = controllers.get(key);
    if (!controller) return;

    controller.destroy();
    controllers.delete(key);
  };

  const init = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((sectionRoot) => {
      mountSection(sectionRoot);
    });

    document.addEventListener('shopify:section:load', (event) => {
      const sectionRoot = getSectionRootFromNode(event.target);
      if (sectionRoot) {
        mountSection(sectionRoot);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const sectionRoot = getSectionRootFromNode(event.target);
      if (sectionRoot) {
        unmountSection(sectionRoot);
      }
    });
  };

  if (!window.ThemeDrawerMenu || !window.ThemeDrawerMenu.initialized) {
    init();
    window.ThemeDrawerMenu = {
      initialized: true,
      mountSection,
      unmountSection
    };
  }
})();



/* --- Migrated from assets/localization-drawer.js --- */

/**
 * Theme Localization Drawer
 * One shared right-side drawer opened by any [data-localization-drawer-toggle].
 */
(() => {
  const DRAWER_SELECTOR = '[data-localization-drawer]';
  const TOGGLE_SELECTOR = '[data-localization-drawer-toggle]';
  const LOCALIZATION_DRAWER_STATE_EVENT = 'theme:localization-drawer:state';
  const LOCAL_LOCK_ATTR = 'data-theme-localization-drawer-lock-count';
  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const SCROLLBAR_VAR = '--theme-scrollbar-compensation';
  const DEFAULT_CLOSE_DURATION = 360;
  const DEFAULT_TAB_TRANSITION_MS = 320;
  const TABS_ENHANCED_CLASS = 'is-tabs-enhanced';
  const TAB_ENTER_FROM_CLASS = 'is-tab-entering';
  const TAB_ENTER_ACTIVE_CLASS = 'is-tab-enter-active';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const parseDurationMs = (rawValue) => {
    if (!rawValue) return NaN;
    const value = rawValue.trim();
    if (value.endsWith('ms')) return Number.parseFloat(value);
    if (value.endsWith('s')) return Number.parseFloat(value) * 1000;
    return Number.parseFloat(value);
  };

  const getMotionDuration = (element) => {
    const style = window.getComputedStyle(element || document.documentElement);
    const duration = parseDurationMs(style.getPropertyValue('--motion-duration-normal'));
    if (Number.isFinite(duration) && duration > 0) {
      return duration;
    }
    return DEFAULT_CLOSE_DURATION;
  };

  const dispatchLocalizationDrawerState = (open, drawer) => {
    document.dispatchEvent(
      new CustomEvent(LOCALIZATION_DRAWER_STATE_EVENT, {
        detail: {
          open: Boolean(open),
          drawerId: drawer?.id || '',
        },
      })
    );
  };

  const getScrollbarCompensation = () => Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const acquireGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty(SCROLLBAR_VAR, compensation);
      body.style.setProperty(SCROLLBAR_VAR, compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty(SCROLLBAR_VAR);
      body.style.removeProperty(SCROLLBAR_VAR);
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  const lockScroll = () => {
    const root = document.documentElement;
    const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      acquireGlobalScrollLock();
    }

    root.setAttribute(LOCAL_LOCK_ATTR, String(current + 1));
  };

  const unlockScroll = () => {
    const root = document.documentElement;
    const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.removeAttribute(LOCAL_LOCK_ATTR);
      releaseGlobalScrollLock();
      return;
    }

    root.setAttribute(LOCAL_LOCK_ATTR, String(next));
  };

  class LocalizationDrawerController {
    constructor() {
      this.drawer = null;
      this.drawerId = '';
      this.panel = null;
      this.overlay = null;
      this.closeButton = null;
      this.tabsWrap = null;
      this.isOpen = false;
      this.opener = null;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.closeTimer = 0;
      this.openStartRafId = 0;
      this.openCommitRafId = 0;
      this.listRevealStartRafId = 0;
      this.listRevealCommitRafId = 0;
      this.listRevealCleanupTimer = 0;
      this.tabEnterCleanupTimer = 0;
      this.closeAnimationMs = DEFAULT_CLOSE_DURATION;
      this.boundDocumentClick = this.onDocumentClick.bind(this);
      this.boundDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.boundDocumentFocusIn = this.onDocumentFocusIn.bind(this);
      this.boundShopifySectionLoad = this.onSectionLifecycle.bind(this);
      this.boundShopifySectionUnload = this.onSectionLifecycle.bind(this);
    }

    init() {
      this.resolveDrawer();
      if (!this.drawer || !this.panel) return false;

      document.addEventListener('click', this.boundDocumentClick);
      document.addEventListener('shopify:section:load', this.boundShopifySectionLoad);
      document.addEventListener('shopify:section:unload', this.boundShopifySectionUnload);
      this.syncToggleState(false);
      return true;
    }

    destroy() {
      window.clearTimeout(this.closeTimer);
      this.cancelPendingOpen();
      this.cancelPendingListReveal();
      window.clearTimeout(this.tabEnterCleanupTimer);
      this.tabEnterCleanupTimer = 0;
      this.getTabPanels().forEach((panelNode) => this.clearTabPanelEnterState(panelNode));
      this.drawer?.classList.remove(TABS_ENHANCED_CLASS);
      document.removeEventListener('click', this.boundDocumentClick);
      document.removeEventListener('keydown', this.boundDocumentKeydown);
      document.removeEventListener('focusin', this.boundDocumentFocusIn);
      document.removeEventListener('shopify:section:load', this.boundShopifySectionLoad);
      document.removeEventListener('shopify:section:unload', this.boundShopifySectionUnload);

      if (this.isOpen) {
        this.teardownOpenState({ returnFocus: false, skipAnimation: true });
      }
    }

    onSectionLifecycle() {
      this.resolveDrawer();
    }

    resolveDrawer() {
      const drawers = Array.from(document.querySelectorAll(DRAWER_SELECTOR));
      if (!drawers.length) {
        this.drawer = null;
        this.panel = null;
        return;
      }

      // When sections hot-reload in the editor, duplicate instances can briefly exist.
      // Always keep the newest one and remove older duplicates.
      const activeDrawer = drawers[drawers.length - 1];
      drawers.forEach((drawerNode) => {
        if (drawerNode !== activeDrawer) {
          drawerNode.remove();
        }
      });

      // Move to <body> to avoid header stacking context overlaps.
      if (activeDrawer.parentElement !== document.body) {
        document.body.appendChild(activeDrawer);
      }

      this.drawer = activeDrawer;
      this.drawerId = this.drawer ? this.drawer.id : '';
      this.panel = this.drawer ? this.drawer.querySelector('[data-localization-drawer-panel]') : null;
      this.overlay = this.drawer ? this.drawer.querySelector('[data-localization-drawer-overlay]') : null;
      this.closeButton = this.drawer ? this.drawer.querySelector('[data-localization-drawer-close]') : null;
      this.tabsWrap = this.drawer ? this.drawer.querySelector('.localization-drawer__tabs') : null;

      if (this.drawer) {
        this.drawer.classList.add(TABS_ENHANCED_CLASS);
        this.closeAnimationMs = getMotionDuration(this.drawer);
      }
    }

    onDocumentClick(event) {
      this.resolveDrawer();
      if (!this.drawer) return;

      const toggle = event.target.closest(TOGGLE_SELECTOR);
      if (toggle && toggle.getAttribute('aria-controls') === this.drawerId) {
        event.preventDefault();
        if (this.isOpen) {
          this.close({ returnFocus: true });
        } else {
          this.open(toggle);
        }
        return;
      }

      if (!this.isOpen) return;

      if (event.target.closest('[data-localization-drawer-close]') || event.target.closest('[data-localization-drawer-overlay]')) {
        event.preventDefault();
        this.close({ returnFocus: true });
        return;
      }

      const tabButton = event.target.closest('[data-localization-tab-target]');
      if (tabButton && this.drawer.contains(tabButton)) {
        event.preventDefault();
        this.setActiveTab(tabButton.getAttribute('data-localization-tab-target'));
      }
    }

    onDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ returnFocus: true });
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.focusElement(this.panel);
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        this.focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        this.focusElement(first);
      }
    }

    onDocumentFocusIn(event) {
      if (!this.isOpen || !this.panel) return;
      if (this.panel.contains(event.target)) return;

      const focusable = this.getFocusableElements();
      if (focusable.length) {
        this.focusElement(focusable[0]);
      } else {
        this.focusElement(this.panel);
      }
    }

    getFocusableElements() {
      if (!this.panel) return [];
      return Array.from(this.panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.hasAttribute('disabled')) return false;
        if (element.closest('[hidden]')) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        if (element.offsetParent === null && window.getComputedStyle(element).position !== 'fixed') return false;
        return true;
      });
    }

    focusElement(element) {
      if (!(element instanceof HTMLElement)) return;
      if (typeof element.focus !== 'function') return;

      try {
        element.focus({ preventScroll: true });
      } catch (_error) {
        element.focus();
      }
    }

    prefersReducedMotion() {
      return this.reducedMotionQuery.matches;
    }

    getTabPanelTransitionMs() {
      if (!this.drawer) return DEFAULT_TAB_TRANSITION_MS;
      const style = window.getComputedStyle(this.drawer);
      const explicitDuration = parseDurationMs(style.getPropertyValue('--tab-panel-transition-duration'));
      if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
        return Math.max(120, Math.min(explicitDuration, 700));
      }

      const fallbackDuration = parseDurationMs(style.getPropertyValue('--motion-duration-normal'));
      if (Number.isFinite(fallbackDuration) && fallbackDuration > 0) {
        return Math.max(120, Math.min(fallbackDuration, 700));
      }

      return DEFAULT_TAB_TRANSITION_MS;
    }

    clearTabPanelEnterState(panel) {
      if (!(panel instanceof HTMLElement)) return;
      panel.classList.remove(TAB_ENTER_FROM_CLASS, TAB_ENTER_ACTIVE_CLASS);
    }

    syncToggleState(isExpanded) {
      if (!this.drawerId) return;
      document.querySelectorAll(`${TOGGLE_SELECTOR}[aria-controls="${this.drawerId}"]`).forEach((toggle) => {
        toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      });
    }

    getTabButton(tab) {
      return this.drawer?.querySelector(`[data-localization-tab-target="${tab}"]`) || null;
    }

    getTabPanel(tab) {
      return this.drawer?.querySelector(`[data-localization-tab-panel="${tab}"]`) || null;
    }

    getTabPanels() {
      return Array.from(this.drawer?.querySelectorAll('[data-localization-tab-panel]') || []);
    }

    getActiveListPanel() {
      return this.getTabPanels().find((panel) => panel.classList.contains('is-active') && !panel.hidden) || null;
    }

    scheduleActivePanelListReveal() {
      const activePanel = this.getActiveListPanel();
      this.schedulePanelListReveal(activePanel);
    }

    schedulePanelListReveal(panel) {
      if (!(panel instanceof HTMLElement)) return;

      panel.classList.remove('is-list-reveal-active');

      const listItems = Array.from(panel.querySelectorAll('.localization-drawer__list > .localization-drawer__item'));
      if (!listItems.length || this.prefersReducedMotion()) return;

      listItems.forEach((item, index) => {
        const staggerIndex = Math.min(index, 12);
        item.style.setProperty('--localization-item-stagger-index', String(staggerIndex));
      });

      this.cancelPendingListReveal();

      this.listRevealStartRafId = window.requestAnimationFrame(() => {
        this.listRevealStartRafId = 0;

        this.listRevealCommitRafId = window.requestAnimationFrame(() => {
          this.listRevealCommitRafId = 0;
          if (!this.isOpen || panel.hidden || !this.drawer?.contains(panel)) return;
          panel.classList.add('is-list-reveal-active');

          const maxStagger = Math.min(listItems.length - 1, 12) * 32;
          this.listRevealCleanupTimer = window.setTimeout(() => {
            this.listRevealCleanupTimer = 0;
            panel.classList.remove('is-list-reveal-active');
          }, 400 + maxStagger + 80);
        });
      });
    }

    applyTriggerPreferences(trigger) {
      const countryPanel = this.getTabPanel('region');
      const languagePanel = this.getTabPanel('language');
      const countryButton = this.getTabButton('region');
      const languageButton = this.getTabButton('language');

      let allowCountry = trigger.dataset.showCountry !== 'false' && Boolean(countryPanel);
      let allowLanguage = trigger.dataset.showLanguage !== 'false' && Boolean(languagePanel);

      if (!allowCountry && !allowLanguage) {
        allowCountry = Boolean(countryPanel);
        allowLanguage = Boolean(languagePanel);
      }

      const showFlags = trigger.dataset.showFlag !== 'false';
      this.drawer.classList.toggle('localization-drawer--hide-flags', !showFlags);

      if (countryButton) {
        countryButton.hidden = !allowCountry;
      }
      if (languageButton) {
        languageButton.hidden = !allowLanguage;
      }
      if (countryPanel) {
        countryPanel.hidden = !allowCountry;
      }
      if (languagePanel) {
        languagePanel.hidden = !allowLanguage;
      }

      if (this.tabsWrap) {
        this.tabsWrap.hidden = !(allowCountry && allowLanguage);
      }

      if (allowCountry) {
        this.setActiveTab('region');
      } else if (allowLanguage) {
        this.setActiveTab('language');
      }
    }

    setActiveTab(tab, { animate = true } = {}) {
      if (!this.drawer) return;
      const countryButton = this.getTabButton('region');
      const languageButton = this.getTabButton('language');
      const countryPanel = this.getTabPanel('region');
      const languagePanel = this.getTabPanel('language');
      const allPanels = this.getTabPanels();
      const previousPanel = allPanels.find((panel) => panel.classList.contains('is-active') && !panel.hidden) || null;

      const canUseCountry = Boolean(countryPanel) && !(countryButton && countryButton.hidden);
      const canUseLanguage = Boolean(languagePanel) && !(languageButton && languageButton.hidden);

      let activeTab = tab;
      if (activeTab === 'region' && !canUseCountry) {
        activeTab = canUseLanguage ? 'language' : 'region';
      }
      if (activeTab === 'language' && !canUseLanguage) {
        activeTab = canUseCountry ? 'region' : 'language';
      }

      window.clearTimeout(this.tabEnterCleanupTimer);
      this.tabEnterCleanupTimer = 0;

      if (countryButton) {
        const isActive = activeTab === 'region' && canUseCountry;
        countryButton.classList.toggle('is-active', isActive);
        countryButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
        countryButton.tabIndex = isActive ? 0 : -1;
      }

      if (languageButton) {
        const isActive = activeTab === 'language' && canUseLanguage;
        languageButton.classList.toggle('is-active', isActive);
        languageButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
        languageButton.tabIndex = isActive ? 0 : -1;
      }

      let activePanel = null;

      if (countryPanel) {
        const isActive = activeTab === 'region' && canUseCountry;
        countryPanel.hidden = !isActive;
        countryPanel.classList.toggle('is-active', isActive);
        if (isActive) {
          activePanel = countryPanel;
        } else {
          this.clearTabPanelEnterState(countryPanel);
        }
      }

      if (languagePanel) {
        const isActive = activeTab === 'language' && canUseLanguage;
        languagePanel.hidden = !isActive;
        languagePanel.classList.toggle('is-active', isActive);
        if (isActive) {
          activePanel = languagePanel;
        } else {
          this.clearTabPanelEnterState(languagePanel);
        }
      }

      const shouldAnimate = Boolean(
        animate &&
          !this.prefersReducedMotion() &&
          this.isOpen &&
          this.drawer?.classList.contains('is-open') &&
          activePanel &&
          previousPanel &&
          activePanel !== previousPanel
      );

      if (shouldAnimate && activePanel) {
        activePanel.classList.add(TAB_ENTER_FROM_CLASS);
        void activePanel.offsetHeight;
        activePanel.classList.remove(TAB_ENTER_FROM_CLASS);
        activePanel.classList.add(TAB_ENTER_ACTIVE_CLASS);
        this.tabEnterCleanupTimer = window.setTimeout(() => {
          this.clearTabPanelEnterState(activePanel);
          this.tabEnterCleanupTimer = 0;
        }, this.getTabPanelTransitionMs() + 40);
      } else if (activePanel) {
        this.clearTabPanelEnterState(activePanel);
      }

      if (this.isOpen && this.drawer?.classList.contains('is-open')) {
        this.scheduleActivePanelListReveal();
      }
    }

    open(trigger) {
      this.resolveDrawer();
      if (this.isOpen || !this.drawer || !this.panel) return;

      this.opener = trigger || document.activeElement;
      this.applyTriggerPreferences(trigger);
      this.syncToggleState(true);

      this.cancelPendingOpen();
      this.cancelPendingListReveal();
      window.clearTimeout(this.closeTimer);
      this.getTabPanels().forEach((panelNode) => panelNode.classList.remove('is-list-reveal-active'));
      this.drawer.classList.remove('is-closing', 'is-opening', 'is-open');
      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      lockScroll();

      this.isOpen = true;
      document.addEventListener('keydown', this.boundDocumentKeydown);
      document.addEventListener('focusin', this.boundDocumentFocusIn);

      this.drawer.classList.add('is-opening');
      dispatchLocalizationDrawerState(true, this.drawer);
      this.openStartRafId = window.requestAnimationFrame(() => {
        this.openStartRafId = 0;
        if (!this.isOpen || !this.drawer) return;

        if (this.panel) void this.panel.offsetHeight;
        void this.drawer.offsetHeight;

        this.openCommitRafId = window.requestAnimationFrame(() => {
          this.openCommitRafId = 0;
          if (!this.isOpen || !this.drawer) return;
          this.drawer.classList.remove('is-opening', 'is-closing');
          this.drawer.classList.add('is-open');
          this.scheduleActivePanelListReveal();
        });
      });

      const initialFocus = this.closeButton || this.getFocusableElements()[0] || this.panel;
      window.requestAnimationFrame(() => this.focusElement(initialFocus));
    }

    close({ returnFocus = true } = {}) {
      if (!this.isOpen || !this.drawer) return;
      this.teardownOpenState({ returnFocus, skipAnimation: false });
    }

    teardownOpenState({ returnFocus = true, skipAnimation = false } = {}) {
      if (!this.drawer) return;

      this.isOpen = false;
      this.syncToggleState(false);
      this.cancelPendingOpen();
      this.cancelPendingListReveal();
      this.getTabPanels().forEach((panel) => panel.classList.remove('is-list-reveal-active'));
      document.removeEventListener('keydown', this.boundDocumentKeydown);
      document.removeEventListener('focusin', this.boundDocumentFocusIn);

      const finishClose = () => {
        this.drawer.classList.remove('is-closing', 'is-opening');
        this.drawer.hidden = true;
        this.drawer.setAttribute('aria-hidden', 'true');
        dispatchLocalizationDrawerState(false, this.drawer);
      };

      if (skipAnimation) {
        window.clearTimeout(this.closeTimer);
        this.drawer.classList.remove('is-open', 'is-closing', 'is-opening');
        finishClose();
      } else {
        if (this.closeAnimationMs <= 0) {
          this.drawer.classList.remove('is-open', 'is-closing', 'is-opening');
          finishClose();
          unlockScroll();
          if (returnFocus && this.opener && typeof this.opener.focus === 'function') {
            this.focusElement(this.opener);
          }
          this.opener = null;
          return;
        }
        this.drawer.classList.add('is-closing');
        this.drawer.classList.remove('is-opening');
        this.drawer.classList.remove('is-open');
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(finishClose, this.closeAnimationMs);
      }

      unlockScroll();

      if (returnFocus && this.opener && typeof this.opener.focus === 'function') {
        this.focusElement(this.opener);
      }
      this.opener = null;
    }

    cancelPendingOpen() {
      if (this.openStartRafId) {
        window.cancelAnimationFrame(this.openStartRafId);
        this.openStartRafId = 0;
      }
      if (this.openCommitRafId) {
        window.cancelAnimationFrame(this.openCommitRafId);
        this.openCommitRafId = 0;
      }
    }

    cancelPendingListReveal() {
      if (this.listRevealStartRafId) {
        window.cancelAnimationFrame(this.listRevealStartRafId);
        this.listRevealStartRafId = 0;
      }
      if (this.listRevealCommitRafId) {
        window.cancelAnimationFrame(this.listRevealCommitRafId);
        this.listRevealCommitRafId = 0;
      }
      if (this.listRevealCleanupTimer) {
        window.clearTimeout(this.listRevealCleanupTimer);
        this.listRevealCleanupTimer = 0;
      }
    }
  }

  const controller = new LocalizationDrawerController();
  if (!controller.init()) return;

  window.ThemeLocalizationDrawer = {
    initialized: true,
    refresh: () => controller.resolveDrawer()
  };
})();



/* --- Migrated from assets/theme-fields.js --- */

(() => {
  const FIELD_SELECTOR = '.field--floating';
  const INPUT_SELECTOR = '.field__input';

  const getFieldInput = (field) => field.querySelector(INPUT_SELECTOR);

  const isInputFilled = (input) => {
    if (!input) return false;

    if (input.tagName === 'SELECT') {
      return input.value !== '';
    }

    return input.value.trim() !== '';
  };

  const updateFieldState = (field) => {
    const input = getFieldInput(field);
    if (!input) return;

    field.classList.toggle('is-filled', isInputFilled(input));
  };

  const bindField = (field) => {
    if (!field || field.dataset.fieldFloatingBound === 'true') return;

    const input = getFieldInput(field);
    if (!input) return;

    field.dataset.fieldFloatingBound = 'true';

    input.addEventListener('focus', () => {
      field.classList.add('is-focused');
    });

    input.addEventListener('blur', () => {
      field.classList.remove('is-focused');
      updateFieldState(field);
    });

    input.addEventListener('input', () => {
      updateFieldState(field);
    });

    input.addEventListener('change', () => {
      updateFieldState(field);
    });

    input.addEventListener('animationstart', (event) => {
      if (event.animationName !== 'theme-field-autofill') return;
      updateFieldState(field);
    });

    requestAnimationFrame(() => {
      updateFieldState(field);
    });
    setTimeout(() => {
      updateFieldState(field);
    }, 180);
  };

  const initFloatingFields = (root = document) => {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll(FIELD_SELECTOR).forEach(bindField);
  };

  const updateAllFloatingFields = (root = document) => {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll(FIELD_SELECTOR).forEach(updateFieldState);
  };

  const onReady = () => initFloatingFields(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target || document;
    initFloatingFields(target);
    updateAllFloatingFields(target);
  });

  window.addEventListener('load', () => {
    updateAllFloatingFields(document);
    setTimeout(() => updateAllFloatingFields(document), 180);
    setTimeout(() => updateAllFloatingFields(document), 480);
  });

  window.addEventListener('pageshow', () => {
    updateAllFloatingFields(document);
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        if (node.matches(FIELD_SELECTOR)) {
          bindField(node);
        }

        initFloatingFields(node);
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();



/* --- Migrated from assets/theme-header.js --- */

(() => {
  const sectionSelector = '.header[data-section-type="header"]';
  const LOCAL_LOCK_ATTR = 'data-theme-header-interaction-lock-count';
  const LOCAL_LOCK_CLASS = 'theme-header-interaction-lock';
  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const SCROLLBAR_VAR = '--theme-scrollbar-compensation';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    '[tabindex]',
    '[contenteditable="true"]',
  ].join(',');
  const ORIGINAL_TABINDEX_ATTR = 'data-header-original-tabindex';

    if (window.ThemeHeaderNav && window.ThemeHeaderNav.initialized) {
      document.querySelectorAll(sectionSelector).forEach((sectionRoot) => {
        window.ThemeHeaderNav.mountSection(sectionRoot);
      });
      return;
    }

    const controllers = new Map();

    const getSectionKey = (sectionRoot) => sectionRoot.getAttribute('data-section-id') || sectionRoot.id || '';

    const getSectionRootFromNode = (node) => {
      if (!node) return null;
      if (node.matches && node.matches(sectionSelector)) return node;
      if (node.querySelector) return node.querySelector(sectionSelector);
      return null;
    };

    const addMediaListener = (mediaQuery, callback, disposers) => {
      if (!mediaQuery) return;

      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', callback);
        disposers.push(() => mediaQuery.removeEventListener('change', callback));
        return;
      }

      if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(callback);
        disposers.push(() => mediaQuery.removeListener(callback));
      }
    };

    const getScrollbarCompensation = () =>
      Math.max(0, window.innerWidth - document.documentElement.clientWidth);

    const acquireGlobalScrollLock = () => {
      const root = document.documentElement;
      const body = document.body;
      const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

      if (current <= 0) {
        const compensation = `${getScrollbarCompensation()}px`;
        root.style.setProperty(SCROLLBAR_VAR, compensation);
        if (body) {
          body.style.setProperty(SCROLLBAR_VAR, compensation);
        }
        root.classList.add(GLOBAL_LOCK_CLASS);
        if (body) {
          body.classList.add(GLOBAL_LOCK_CLASS);
        }
      }

      root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
    };

    const releaseGlobalScrollLock = () => {
      const root = document.documentElement;
      const body = document.body;
      const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
      const next = Math.max(0, current - 1);

      if (next === 0) {
        root.classList.remove(GLOBAL_LOCK_CLASS);
        if (body) {
          body.classList.remove(GLOBAL_LOCK_CLASS);
        }
        root.removeAttribute(GLOBAL_LOCK_ATTR);
        root.style.removeProperty(SCROLLBAR_VAR);
        if (body) {
          body.style.removeProperty(SCROLLBAR_VAR);
        }
        return;
      }

      root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
    };

    const initHeaderNav = (sectionRoot) => {
      if (!sectionRoot) return () => {};

      const nav = sectionRoot.querySelector('.header__nav');
      const menuItems = nav ? Array.from(nav.querySelectorAll('details[data-has-submenu="true"]')) : [];
      const searchToggle = sectionRoot.querySelector('[data-header-search-toggle]');
      const searchPanel = sectionRoot.querySelector('[data-header-search-panel]');
      const searchForm = searchPanel ? searchPanel.querySelector('[data-header-search-form]') : null;
      const searchInput = searchPanel ? searchPanel.querySelector('[data-header-search-input]') : null;
      const searchClear = searchPanel ? searchPanel.querySelector('[data-header-search-clear]') : null;
      const searchClose = searchPanel ? searchPanel.querySelector('[data-header-search-close]') : null;
      const searchResults = sectionRoot.querySelector('[data-header-search-results]');
      const megaLayer = sectionRoot.querySelector('[data-header-mega-layer]');
      const interactionBackdrop = sectionRoot.querySelector('[data-header-interaction-backdrop]');
      const sectionShell = sectionRoot.closest('.shopify-section');
      const overlayEnabled = sectionRoot.dataset.overlayEnabled === 'true';
      const overlaySelector = sectionRoot.dataset.overlaySelector || '[data-hero-section]';
      // Treat the global breadcrumb bar as a section that precedes the template
      // content. When it is present the header must stay solid (no overlay), so
      // the transparent nav never overlaps the breadcrumb. The breadcrumb is
      // rendered server-side and never moves, so the presence check is cached.
      const globalBreadcrumb = document.querySelector('.theme-breadcrumbs');
      const hasGlobalBreadcrumb = Boolean(globalBreadcrumb && !globalBreadcrumb.closest('#MainContent'));
      const stickyMode = sectionRoot.dataset.stickyMode || 'none';
      const stickyModeClasses = ['header--sticky-none', 'header--sticky-scroll-up', 'header--sticky-always'];

      const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
      const widthMedia = window.matchMedia('(min-width: 1000px)');
      const isHoverMode = () => hoverMedia.matches && widthMedia.matches;

      const disposers = [];
      let openTimer = 0;
      let closeTimer = 0;
      let switchTimer = 0;
      let activeItem = menuItems.find((item) => item.hasAttribute('open')) || null;
      let searchOpen = false;
      let searchResultsOpen = false;
      let overlayInteractionActive = false;
      let stickyForceDefaultScheme = false;
      let scrollUpStickyActivated = false;
      let lastScrollY = window.scrollY || window.pageYOffset || 0;
      let stickyTicking = false;

      // Sticky scroll-up tuning. Activation threshold is the Y position
      // (in px) at which scroll-up mode "arms" — below this the header
      // behaves as if non-sticky. The hide/show deltas are hysteresis
      // thresholds that prevent flip-flop from inertial / smooth scroll
      // micro-movements: the user must travel at least this far past the
      // last direction-reversal anchor before the visible state flips.
      const STICKY_ACTIVATION_OFFSET = 96;
      const STICKY_HIDE_DELTA_PX = 8;
      const STICKY_SHOW_DELTA_PX = 6;

      // Hysteresis state for scroll_up mode.
      let stickyAnchorY = lastScrollY;
      let stickyLastDeltaSign = 0;

      // Cached sticky header height (kept in sync via ResizeObserver) so
      // syncStickyVisibleOffset doesn't read the DOM on every scroll tick.
      let cachedStickyHeight = 0;
      let stickyHeightObserver = null;

      // Cached CSS-var values so we can early-return on redundant writes.
      // These three properties live on documentElement + body and were
      // previously written on every scroll tick.
      let lastVisibleOffsetPx = -1;
      let lastSafeOffsetPx = -1;
      let lastHeaderLockTopPx = -1;

      let headerInteractionScrollLocked = false;
      let drawerStateHintOpen = false;

      const lockHeaderInteractionScroll = () => {
        if (headerInteractionScrollLocked) return;

        const root = document.documentElement;
        const body = document.body;
        const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);

        if (current <= 0) {
          root.classList.add(LOCAL_LOCK_CLASS);
          if (body) {
            body.classList.add(LOCAL_LOCK_CLASS);
          }
          acquireGlobalScrollLock();
        }

        root.setAttribute(LOCAL_LOCK_ATTR, String(current + 1));
        headerInteractionScrollLocked = true;
      };

      const unlockHeaderInteractionScroll = () => {
        if (!headerInteractionScrollLocked) return;

        const root = document.documentElement;
        const body = document.body;
        const current = Math.max(0, Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10));
        const next = Math.max(0, current - 1);

        if (next === 0) {
          root.classList.remove(LOCAL_LOCK_CLASS);
          if (body) {
            body.classList.remove(LOCAL_LOCK_CLASS);
          }
          root.removeAttribute(LOCAL_LOCK_ATTR);
          releaseGlobalScrollLock();
        } else {
          root.setAttribute(LOCAL_LOCK_ATTR, String(next));
        }

        headerInteractionScrollLocked = false;
      };

      const syncHeaderInteractionScrollLock = () => {
        const hasOpenMega = Boolean(
          activeItem
          && activeItem.hasAttribute('open')
          && isMegaItem(activeItem)
        );

        const shouldLock = Boolean(
          stickyMode === 'none'
          && isHoverMode()
          && (searchOpen || hasOpenMega)
        );

        if (shouldLock) {
          lockHeaderInteractionScroll();
          return;
        }

        unlockHeaderInteractionScroll();
      };

      const isDrawerVisiblyOpen = (drawerNode) => {
        if (!(drawerNode instanceof HTMLElement)) return false;
        if (drawerNode.hidden || drawerNode.hasAttribute('hidden')) return false;
        if (drawerNode.getAttribute('aria-hidden') === 'true') return false;
        return drawerNode.classList.contains('is-open') || drawerNode.classList.contains('is-opening');
      };

      const hasActiveDrawer = () => {
        const selectors = [
          '[data-drawer-menu]',
          '[data-localization-drawer]',
          '[data-cart-drawer]',
          '[data-search-drawer]',
        ];

        return selectors.some((selector) =>
          Array.from(document.querySelectorAll(selector)).some((drawerNode) => isDrawerVisiblyOpen(drawerNode))
        );
      };

      const isDrawerOpenForSticky = () => drawerStateHintOpen || hasActiveDrawer();

      const on = (target, eventName, handler, options) => {
        if (!target) return;
        target.addEventListener(eventName, handler, options);
        disposers.push(() => target.removeEventListener(eventName, handler, options));
      };

      const setContainerInertState = (container, shouldDisable) => {
        if (!(container instanceof HTMLElement)) return;
        if ('inert' in container) {
          container.inert = shouldDisable;
        }
      };

      const syncFocusableElements = (container, shouldDisable) => {
        if (!(container instanceof HTMLElement)) return;

        container.querySelectorAll(FOCUSABLE_SELECTOR).forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (shouldDisable) {
            if (!node.hasAttribute(ORIGINAL_TABINDEX_ATTR)) {
              const tabindex = node.getAttribute('tabindex');
              if (tabindex === null) {
                node.setAttribute(ORIGINAL_TABINDEX_ATTR, '');
              } else {
                node.setAttribute(ORIGINAL_TABINDEX_ATTR, tabindex);
              }
            }
            node.setAttribute('tabindex', '-1');
            return;
          }

          if (!node.hasAttribute(ORIGINAL_TABINDEX_ATTR)) return;

          const originalTabindex = node.getAttribute(ORIGINAL_TABINDEX_ATTR);
          if (originalTabindex === '') {
            node.removeAttribute('tabindex');
          } else if (originalTabindex !== null) {
            node.setAttribute('tabindex', originalTabindex);
          }
          node.removeAttribute(ORIGINAL_TABINDEX_ATTR);
        });
      };

      const getTrigger = (item) => item.querySelector('[data-menu-trigger]');

      const clearTimers = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
        window.clearTimeout(switchTimer);
      };

      // Toggle a sticky-related class on both sectionRoot and sectionShell.
      // Returns true if anything actually changed — callers use the return
      // value to skip the (relatively expensive) syncStickyVisibleOffset()
      // call on no-op toggles (e.g. every other scroll tick before the
      // hysteresis threshold is crossed).
      const syncStickyClass = (className, enabled) => {
        let changed = false;
        if (sectionRoot.classList.contains(className) !== enabled) {
          sectionRoot.classList.toggle(className, enabled);
          changed = true;
        }
        if (sectionShell && sectionShell.classList.contains(className) !== enabled) {
          sectionShell.classList.toggle(className, enabled);
          changed = true;
        }
        return changed;
      };

      const setStickyVisibleOffset = (value) => {
        const normalized = Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
        if (normalized === lastVisibleOffsetPx) return;
        lastVisibleOffsetPx = normalized;
        const offsetValue = `${normalized}px`;

        document.documentElement.style.setProperty('--sticky-header-visible-offset', offsetValue);
        document.documentElement.style.setProperty('--theme-sticky-header-offset', offsetValue);

        if (document.body) {
          document.body.style.setProperty('--sticky-header-visible-offset', offsetValue);
          document.body.style.setProperty('--theme-sticky-header-offset', offsetValue);
        }
      };

      const setHeaderSafeOffset = (value) => {
        const normalized = Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
        if (normalized === lastSafeOffsetPx) return;
        lastSafeOffsetPx = normalized;
        const offsetValue = `${normalized}px`;

        document.documentElement.style.setProperty('--theme-header-safe-offset', offsetValue);

        if (document.body) {
          document.body.style.setProperty('--theme-header-safe-offset', offsetValue);
        }
      };

      const setHeaderLockTopOffset = (value) => {
        const normalized = Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
        if (normalized === lastHeaderLockTopPx) return;
        lastHeaderLockTopPx = normalized;
        const offsetValue = `${normalized}px`;

        document.documentElement.style.setProperty('--theme-header-lock-top', offsetValue);

        if (document.body) {
          document.body.style.setProperty('--theme-header-lock-top', offsetValue);
        }
      };

      const getAnnouncementVisibleHeight = (currentY) => {
        if (currentY > 0) return 0;

        const announcementBar = document.querySelector('[data-announcement-bar]');
        if (!(announcementBar instanceof HTMLElement)) return 0;

        const measurementNode = announcementBar.closest('.shopify-section') || announcementBar;
        const rect = measurementNode.getBoundingClientRect();
        if (!Number.isFinite(rect.height) || rect.height <= 0) return 0;

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const visibleTop = Math.max(rect.top, 0);
        const visibleBottom = Math.min(rect.bottom, viewportHeight || rect.bottom);
        const visibleHeight = visibleBottom - visibleTop;

        if (!Number.isFinite(visibleHeight) || visibleHeight <= 0) return 0;
        return Math.round(visibleHeight);
      };

      const getStickyHeaderHeight = () => {
        const headerInner = sectionRoot.querySelector('.header__inner');
        const candidates = [
          sectionShell ? sectionShell.offsetHeight : 0,
          sectionShell && typeof sectionShell.getBoundingClientRect === 'function' ? sectionShell.getBoundingClientRect().height : 0,
          sectionRoot.offsetHeight,
          typeof sectionRoot.getBoundingClientRect === 'function' ? sectionRoot.getBoundingClientRect().height : 0,
          headerInner ? headerInner.offsetHeight : 0,
          headerInner && typeof headerInner.getBoundingClientRect === 'function' ? headerInner.getBoundingClientRect().height : 0,
        ].filter((value) => Number.isFinite(value) && value > 0);

        if (candidates.length === 0) return 0;
        return Math.round(Math.max(...candidates));
      };

      const hasStickyClass = (className) => {
        if (sectionShell && sectionShell.classList.contains(className)) return true;
        return sectionRoot.classList.contains(className);
      };

      // Recompute and cache the sticky header height. Called from the
      // ResizeObserver below + on resize/state transitions. The cache lets
      // syncStickyVisibleOffset avoid up to 6 layout reads (offsetHeight x3,
      // getBoundingClientRect x3) on every scroll tick.
      const refreshStickyHeightCache = () => {
        cachedStickyHeight = getStickyHeaderHeight();
      };

      // Initial measurement so the first scroll tick has a value to use.
      refreshStickyHeightCache();

      // Observe the header surfaces for size changes (font swap, image
      // swap, viewport orientation change, container queries, theme editor
      // edits). Falls back gracefully when ResizeObserver is unavailable.
      if (typeof ResizeObserver === 'function') {
        stickyHeightObserver = new ResizeObserver(() => {
          refreshStickyHeightCache();
          syncStickyVisibleOffset();
        });
        if (sectionShell) stickyHeightObserver.observe(sectionShell);
        stickyHeightObserver.observe(sectionRoot);
        const headerInner = sectionRoot.querySelector('.header__inner');
        if (headerInner instanceof HTMLElement) {
          stickyHeightObserver.observe(headerInner);
        }
        disposers.push(() => {
          if (stickyHeightObserver) {
            stickyHeightObserver.disconnect();
            stickyHeightObserver = null;
          }
        });
      }

      const syncStickyVisibleOffset = () => {
        const currentY = window.scrollY || window.pageYOffset || 0;
        const isHidden = hasStickyClass('header--sticky-hidden');
        // Prefer the cached value from the ResizeObserver — falls back to a
        // live read only on the first call before the observer has fired.
        const stickyHeaderHeight = cachedStickyHeight || getStickyHeaderHeight();
        let stickyIsActive = false;
        let offset = 0;

        if (stickyMode === 'always') {
          stickyIsActive = true;
          offset = currentY > 0 && !isHidden ? stickyHeaderHeight : 0;
        } else if (stickyMode === 'scroll_up') {
          stickyIsActive = hasStickyClass('header--sticky-active');
          offset = currentY > 0 && stickyIsActive && !isHidden ? stickyHeaderHeight : 0;
        }

        let overlayOffset = 0;
        const overlayReady = sectionRoot.classList.contains('header--overlay-ready');
        if (overlayEnabled && overlayReady) {
          if (stickyMode === 'none' || stickyMode === 'always') {
            overlayOffset = stickyHeaderHeight;
          } else if (stickyMode === 'scroll_up') {
            const beforeScrollUpActivation = currentY <= STICKY_ACTIVATION_OFFSET;
            const stickyVisible = stickyIsActive && !isHidden;
            if (beforeScrollUpActivation || stickyVisible) {
              overlayOffset = stickyHeaderHeight;
            }
          }
        }

        const safeOffset = Math.max(offset, overlayOffset);
        const headerLockTopOffset = getAnnouncementVisibleHeight(currentY);

        if (document.body) {
          document.body.classList.toggle('theme-header-sticky-active', stickyIsActive);
          document.body.classList.toggle('theme-header-sticky-visible', offset > 0);
          document.body.classList.toggle('theme-header-overlay-visible', overlayOffset > 0);
          document.body.classList.toggle('theme-header-safe-offset-active', safeOffset > 0);
        }

        setStickyVisibleOffset(offset);
        setHeaderSafeOffset(safeOffset);
        setHeaderLockTopOffset(headerLockTopOffset);
      };

      const syncStickyModeClass = () => {
        if (!sectionShell) return;
        stickyModeClasses.forEach((className) => sectionShell.classList.remove(className));
        if (stickyMode === 'scroll_up') {
          sectionShell.classList.add('header--sticky-scroll-up');
        } else if (stickyMode === 'always') {
          sectionShell.classList.add('header--sticky-always');
        } else {
          sectionShell.classList.add('header--sticky-none');
        }
        syncStickyVisibleOffset();
      };

      const setStickyForceDefaultScheme = (shouldForce) => {
        if (stickyForceDefaultScheme === shouldForce) return;
        stickyForceDefaultScheme = shouldForce;
        updateOverlayState();
      };

      const getFirstTemplateSection = () => {
        // Theme shell wraps template output in #MainContent.
        // Overlay eligibility must inspect the first section inside main first.
        const mainContent = document.getElementById('MainContent');
        if (mainContent) {
          const firstTemplateSection = mainContent.querySelector('.shopify-section');
          if (firstTemplateSection) return firstTemplateSection;
        }

        const headerSection = sectionRoot.closest('.shopify-section');
        if (!headerSection) return null;

        let sibling = headerSection.nextElementSibling;
        while (sibling) {
          if (sibling.classList && sibling.classList.contains('shopify-section')) {
            return sibling;
          }
          sibling = sibling.nextElementSibling;
        }

        return null;
      };

      const isOverlayEligible = () => {
        if (!overlayEnabled) return false;
        if (hasGlobalBreadcrumb) return false;
        const firstSection = getFirstTemplateSection();
        if (!firstSection) return false;
        return Boolean(firstSection.matches(overlaySelector) || firstSection.querySelector(overlaySelector));
      };

      const updateOverlayState = () => {
        if (!overlayEnabled) return;

        const eligible = isOverlayEligible();
        sectionRoot.classList.toggle('header--overlay-ready', eligible);

        if (!eligible) {
          sectionRoot.classList.remove('header--overlay-active');
          sectionRoot.classList.remove('header--overlay-solid');
          return;
        }

        const hasOpenItem = menuItems.some((item) => item.hasAttribute('open'));
        const shouldUseDefaultScheme = overlayInteractionActive || hasOpenItem || stickyForceDefaultScheme || searchOpen;

        sectionRoot.classList.toggle('header--overlay-active', !shouldUseDefaultScheme);
        sectionRoot.classList.toggle('header--overlay-solid', shouldUseDefaultScheme);
        syncStickyVisibleOffset();
      };

      const setOverlayInteraction = (isActive) => {
        if (!overlayEnabled) return;
        if (overlayInteractionActive === isActive) return;
        overlayInteractionActive = isActive;
        updateOverlayState();
      };

      const applyStickyState = () => {
        const currentY = window.scrollY || window.pageYOffset || 0;

        // 1. Compute the desired final state for this tick — no DOM writes yet.
        let nextActive = false;
        let nextHidden = false;
        let nextForceDefaultScheme = false;

        if (stickyMode === 'none') {
          // active=false, hidden=false (defaults)
        } else if (stickyMode === 'always') {
          nextActive = currentY > 0;
          nextForceDefaultScheme = nextActive;
        } else {
          // scroll_up
          if (currentY <= 0) {
            // Reset hysteresis state at the very top. Anchor stays at 0
            // so the first downward movement past HIDE_DELTA still feels
            // intentional rather than instant.
            scrollUpStickyActivated = false;
            stickyAnchorY = currentY;
            stickyLastDeltaSign = 0;
          } else {
            if (currentY > STICKY_ACTIVATION_OFFSET) {
              scrollUpStickyActivated = true;
            }

            if (scrollUpStickyActivated) {
              nextActive = true;
              nextForceDefaultScheme = true;

              const drawerOpen = isDrawerOpenForSticky();
              const blockHide = Boolean(activeItem) || searchOpen || drawerOpen;

              // Start from the current visible state so unchanged ticks
              // (within the hysteresis dead-zone) don't flip the class.
              nextHidden = hasStickyClass('header--sticky-hidden');

              if (blockHide) {
                // Drawer / search / mega-menu interactions force the header
                // visible AND reset the hysteresis anchor so the first
                // movement after they close starts from a clean baseline.
                nextHidden = false;
                stickyAnchorY = currentY;
                stickyLastDeltaSign = 0;
              } else {
                const delta = currentY - lastScrollY;
                const sign = delta > 0 ? 1 : (delta < 0 ? -1 : 0);

                // Reversal: anchor at the previous Y so the deltas measured
                // for show/hide are taken from the *peak* of the prior run,
                // not from a midway sample.
                if (sign !== 0 && stickyLastDeltaSign !== 0 && sign !== stickyLastDeltaSign) {
                  stickyAnchorY = lastScrollY;
                }

                const fromAnchor = currentY - stickyAnchorY;
                if (fromAnchor >= STICKY_HIDE_DELTA_PX) {
                  nextHidden = true;
                } else if (fromAnchor <= -STICKY_SHOW_DELTA_PX) {
                  nextHidden = false;
                }
                // Movement smaller than the deltas in either direction is
                // ignored — that's the dead-zone that prevents jitter from
                // inertial / smooth scroll oscillation.

                if (sign !== 0) stickyLastDeltaSign = sign;
              }
            }
          }
        }

        // 2. Apply class states (idempotent — no work if unchanged).
        syncStickyClass('header--sticky-active', nextActive);
        syncStickyClass('header--sticky-hidden', nextHidden);
        setStickyForceDefaultScheme(nextForceDefaultScheme);

        // 3. Run the offset sync once at the end. With cached header
        // height + early-return CSS-var setters, this is a cheap function
        // when nothing has changed — the cost shows up only on the ticks
        // that actually transition state.
        syncStickyVisibleOffset();

        lastScrollY = currentY;
      };

      const scheduleStickyState = () => {
        if (stickyMode === 'none') return;
        if (stickyTicking) return;

        stickyTicking = true;
        window.requestAnimationFrame(() => {
          applyStickyState();
          stickyTicking = false;
        });
      };

      const syncExpanded = (item) => {
        const trigger = getTrigger(item);
        if (!trigger) return;
        trigger.setAttribute('aria-expanded', item.hasAttribute('open') ? 'true' : 'false');
      };

      const syncAllExpanded = () => {
        menuItems.forEach((item) => syncExpanded(item));
      };

      const syncSearchState = () => {
        if (!searchToggle || !searchPanel) return;
        sectionRoot.classList.toggle('header--search-open', searchOpen);
        searchToggle.setAttribute('aria-expanded', searchOpen ? 'true' : 'false');
        searchPanel.setAttribute('aria-hidden', searchOpen ? 'false' : 'true');
        setContainerInertState(searchPanel, !searchOpen);
        syncFocusableElements(searchPanel, !searchOpen);
        if (searchInput) {
          searchInput.setAttribute('aria-expanded', searchOpen && searchResultsOpen ? 'true' : 'false');
        }
      };

      const syncSearchResultsState = () => {
        if (!searchResults) return;
        const shouldShowResults = searchOpen && searchResultsOpen;
        sectionRoot.classList.toggle('header--search-results-open', shouldShowResults);
        searchResults.setAttribute('aria-hidden', shouldShowResults ? 'false' : 'true');
        setContainerInertState(searchResults, !shouldShowResults);
        syncFocusableElements(searchResults, !shouldShowResults);
        if (searchInput) {
          searchInput.setAttribute('aria-expanded', shouldShowResults ? 'true' : 'false');
        }
      };

      const setSearchResultsOpen = (open) => {
        if (!searchResults) return;
        searchResultsOpen = open;
        syncSearchResultsState();
      };

      const setSearchOpen = (open, options = {}) => {
        if (!searchToggle || !searchPanel) return;
        if (searchOpen === open) {
          if (open && options.focusInput && searchInput) {
            window.requestAnimationFrame(() => searchInput.focus({ preventScroll: true }));
          }
          return;
        }

        const activeElement = document.activeElement;
        const focusIsInSearch = Boolean(
          activeElement
          && (
            (searchPanel && searchPanel.contains(activeElement))
            || (searchResults && searchResults.contains(activeElement))
          )
        );

        searchOpen = open;
        if (!searchOpen) {
          setSearchResultsOpen(false);
        }
        syncSearchState();
        syncSearchResultsState();

        if (searchOpen) {
          if (stickyMode === 'scroll_up') {
            syncStickyClass('header--sticky-hidden', false);
          }
          if (options.focusInput !== false && searchInput) {
            window.requestAnimationFrame(() => searchInput.focus({ preventScroll: true }));
          }
        } else if (focusIsInSearch && searchToggle) {
          window.requestAnimationFrame(() => searchToggle.focus({ preventScroll: true }));
        }

        scheduleStickyState();
        updateOverlayState();
        syncHeaderInteractionScrollLock();
        sectionRoot.dispatchEvent(
          new CustomEvent('theme:header-search:state', {
            detail: {
              open: searchOpen,
              sectionId: getSectionKey(sectionRoot),
            },
          })
        );
      };

      const isMegaItem = (item) => item && item.classList.contains('header-nav__details--mega');

      const getMs = (str) => {
        if (!str) return 0;
        const s = str.trim();
        if (s.endsWith('ms')) return parseFloat(s);
        if (s.endsWith('s')) return parseFloat(s) * 1000;
        return parseFloat(s) || 0;
      };

      const rootStyles = getComputedStyle(sectionRoot);
      const contentEnterDelay = getMs(rootStyles.getPropertyValue('--mega-content-enter-delay')) || 360;
      const contentDuration   = getMs(rootStyles.getPropertyValue('--mega-content-duration'))   || 220;
      const fastDuration      = getMs(rootStyles.getPropertyValue('--motion-duration-fast'))     || 180;
      const motionEase        = rootStyles.getPropertyValue('--motion-ease').trim() || 'ease';
      const reducedMotion     = window.matchMedia('(prefers-reduced-motion: reduce)');

      const getMegaRevealTargets = (layout) => {
        if (!layout) return [];

        if (layout.classList.contains('mega-menu__layout--columns')) {
          return Array.from(layout.querySelectorAll('.mega-menu__cols > .mega-menu__col'));
        }

        if (layout.classList.contains('mega-menu__layout--submenu')) {
          const primaryColumn = layout.querySelector('.mega-menu__col');
          const featuredCards = Array.from(layout.querySelectorAll('.mega-menu__featured > .mega-menu__card'));
          return [primaryColumn, ...featuredCards].filter(Boolean);
        }

        return Array.from(layout.children || []);
      };

      const cancelContentEnter = (item) => {
        if (!item) return;
        const layout = item.querySelector('.mega-menu__layout');
        if (!layout) return;
        layout.getAnimations().forEach((a) => a.cancel());
        getMegaRevealTargets(layout).forEach((target) => {
          target.getAnimations().forEach((a) => a.cancel());
        });
      };

      const triggerContentEnter = (item, fast = false) => {
        if (!item) return;
        const layout = item.querySelector('.mega-menu__layout');
        if (!layout) return;

        layout.getAnimations().forEach((a) => a.cancel());
        const revealTargets = getMegaRevealTargets(layout);
        revealTargets.forEach((target) => {
          target.getAnimations().forEach((a) => a.cancel());
        });

        if (reducedMotion.matches) return;

        const enterDelay = fast ? Math.round(contentEnterDelay / 3) : contentEnterDelay;
        const columnDuration = fast ? Math.max(140, Math.round(contentDuration * 0.85)) : Math.max(220, Math.round(contentDuration * 1.25));
        const columnStagger = fast ? 34 : 56;

        layout.animate(
          [
            { opacity: 0, transform: 'translateY(6px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          {
            duration: fast ? fastDuration : contentDuration,
            delay: enterDelay,
            easing:   motionEase,
            fill:     'both',
          }
        );

        revealTargets.forEach((target, index) => {
          target.animate(
            [
              { opacity: 0, transform: 'translateY(10px)' },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            {
              duration: columnDuration,
              delay: enterDelay + (index * columnStagger),
              easing: motionEase,
              fill: 'both',
            }
          );
        });
      };

      const closeMegaLayer = () => {
        if (!megaLayer) return;
        sectionRoot.classList.remove('header--mega-layer-open');
        sectionRoot.style.setProperty('--header-mega-layer-height', '0px');
        sectionRoot.style.setProperty('--header-mega-layer-offset', '0px');
        megaLayer.setAttribute('aria-hidden', 'true');
      };

      const syncMegaLayer = (item = activeItem) => {
        if (!megaLayer) return;

        window.requestAnimationFrame(() => {
          sectionRoot.querySelectorAll('.mega-menu__inner.mega-menu__inner--scrollable').forEach((innerNode) => {
            innerNode.classList.remove('mega-menu__inner--scrollable');
          });

          if (!item || !item.hasAttribute('open') || !isMegaItem(item)) {
            closeMegaLayer();
            return;
          }

          const megaInner = item.querySelector('.mega-menu__inner');
          if (!megaInner) {
            closeMegaLayer();
            return;
          }

          const measuredHeight = Math.max(1, megaInner.scrollHeight + 2);
          const layerOffset = sectionRoot.classList.contains('header--overlay') ? sectionRoot.offsetHeight : 0;
          const layerHeight = Math.max(1, measuredHeight + layerOffset);
          sectionRoot.style.setProperty('--header-mega-layer-offset', `${layerOffset}px`);
          sectionRoot.style.setProperty('--header-mega-layer-max-height', `${layerHeight}px`);
          sectionRoot.style.setProperty('--header-mega-layer-height', `${layerHeight}px`);
          sectionRoot.classList.add('header--mega-layer-open');
          megaLayer.setAttribute('aria-hidden', 'false');
        });
      };

      const closeItem = (item) => {
        if (!item) return;
        cancelContentEnter(item);
        item.removeAttribute('open');
        syncExpanded(item);

        if (activeItem === item) {
          activeItem = null;
        }

        syncMegaLayer(activeItem);
        updateOverlayState();
        syncHeaderInteractionScrollLock();
      };

      const closeAll = () => {
        menuItems.forEach((item) => {
          cancelContentEnter(item);
          item.removeAttribute('open');
        });
        activeItem = null;
        syncAllExpanded();
        sectionRoot.classList.remove('header--mega-switching');
        closeMegaLayer();
        updateOverlayState();
        syncHeaderInteractionScrollLock();
      };

      const markMegaSwitching = (nextItem) => {
        const activeIsMega = activeItem && activeItem.classList.contains('header-nav__details--mega');
        const nextIsMega = nextItem && nextItem.classList.contains('header-nav__details--mega');

        if (activeItem && nextItem && activeItem !== nextItem && activeIsMega && nextIsMega) {
          sectionRoot.classList.add('header--mega-switching');
          window.clearTimeout(switchTimer);
          switchTimer = window.setTimeout(() => {
            sectionRoot.classList.remove('header--mega-switching');
          }, 260);
          return true;
        }

        sectionRoot.classList.remove('header--mega-switching');
        return false;
      };

      const openItem = (item) => {
        if (!item) return;
        setSearchOpen(false, { focusInput: false });

        if (activeItem === item && item.hasAttribute('open')) {
          syncExpanded(item);
          syncMegaLayer(item);
          return;
        }

        const isSwitching = markMegaSwitching(item);

        menuItems.forEach((node) => {
          if (node === item) {
            node.setAttribute('open', '');
            activeItem = node;
          } else {
            cancelContentEnter(node);
            node.removeAttribute('open');
          }
          syncExpanded(node);
        });

        triggerContentEnter(item, isSwitching);
        syncMegaLayer(activeItem);
        if (stickyMode === 'scroll_up') {
          syncStickyClass('header--sticky-hidden', false);
        }
        updateOverlayState();
        syncHeaderInteractionScrollLock();
      };

      const toggleItem = (item) => {
        if (!item) return;
        if (item.hasAttribute('open')) {
          closeItem(item);
        } else {
          openItem(item);
        }
      };

      const isNodeHovered = (node) => Boolean(node && node.matches(':hover'));

      const isItemFocusRetained = (item) => {
        if (!item) return false;
        const activeElement = document.activeElement;
        return Boolean(activeElement && item.contains(activeElement));
      };

      const shouldKeepItemOpen = (item) => {
        if (!item) return false;
        return isNodeHovered(item) || isItemFocusRetained(item);
      };

      const shouldKeepAnyMenuOpen = () => {
        const activeElement = document.activeElement;
        const navHasFocus = Boolean(activeElement && nav && nav.contains(activeElement));
        const activeItemHasFocus = Boolean(activeElement && activeItem && activeItem.contains(activeElement));

        return Boolean(
          isNodeHovered(nav)
          || isNodeHovered(activeItem)
          || navHasFocus
          || activeItemHasFocus
        );
      };

      const hoverOpenDelay = 90;
      const hoverCloseDelay = 180;

      const scheduleOpen = (item) => {
        window.clearTimeout(closeTimer);
        window.clearTimeout(openTimer);
        openTimer = window.setTimeout(() => {
          openItem(item);
        }, hoverOpenDelay);
      };

      const scheduleClose = (item) => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (!isHoverMode()) return;
          if (item && item === activeItem) {
            if (shouldKeepItemOpen(item)) return;
            closeItem(item);
          }
        }, hoverCloseDelay);
      };

      const scheduleCloseAll = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (!isHoverMode()) return;
          if (shouldKeepAnyMenuOpen()) return;
          closeAll();
        }, hoverCloseDelay);
      };

      menuItems.forEach((item) => {
        const trigger = getTrigger(item);
        if (!trigger) return;

        let keyboardTogglePending = false;
        syncExpanded(item);

        on(trigger, 'keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

          event.preventDefault();
          keyboardTogglePending = true;
          toggleItem(item);
        });

        on(trigger, 'click', (event) => {
          if (keyboardTogglePending) {
            keyboardTogglePending = false;
            event.preventDefault();
            return;
          }

          const navUrl = trigger.getAttribute('data-nav-url');

          if (isHoverMode()) {
            event.preventDefault();
            if (navUrl && navUrl !== '#') {
              window.location.href = navUrl;
            }
            return;
          }

          event.preventDefault();
          toggleItem(item);
        });

        on(trigger, 'mouseenter', () => {
          if (!isHoverMode()) return;
          setOverlayInteraction(true);
          scheduleOpen(item);
        });

        on(item, 'mouseleave', () => {
          if (!isHoverMode()) return;
          scheduleClose(item);
        });

        on(item, 'focusin', () => {
          setOverlayInteraction(true);
          window.clearTimeout(closeTimer);
          openItem(item);
        });

        on(item, 'focusout', (event) => {
          if (item.contains(event.relatedTarget)) return;
          if (!isHoverMode()) return;
          scheduleClose(item);
        });
      });

      if (searchToggle && searchPanel) {
        syncSearchState();
        syncSearchResultsState();
        if (searchClear && searchInput) {
          searchClear.hidden = searchInput.value.length === 0;
        }

        on(searchToggle, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (!searchOpen) {
            closeAll();
          }

          setSearchOpen(!searchOpen, { focusInput: true });
        });

        on(searchPanel, 'click', (event) => {
          event.stopPropagation();
        });

        on(sectionRoot, 'theme:predictive-search:show', () => {
          if (!searchOpen) return;
          setSearchResultsOpen(true);
        });

        on(sectionRoot, 'theme:predictive-search:hide', () => {
          // Keep panel open while the input has focus so search feels "open"
          // immediately on click/focus, even before predictive results return.
          if (
            searchOpen
            && searchInput
            && document.activeElement === searchInput
            && searchInput.value.trim().length === 0
          ) {
            setSearchResultsOpen(true);
            return;
          }
          setSearchResultsOpen(false);
        });

        if (searchInput) {
          on(searchInput, 'focus', () => {
            if (!searchOpen) {
              setSearchOpen(true, { focusInput: false });
            }
            setSearchResultsOpen(true);
          });

          on(searchInput, 'click', () => {
            if (!searchOpen) {
              setSearchOpen(true, { focusInput: false });
            }
            if (!searchResultsOpen) {
              setSearchResultsOpen(true);
            }
          });

          on(searchInput, 'input', () => {
            if (searchClear) {
              searchClear.hidden = searchInput.value.length === 0;
            }
          });
        }

        if (searchForm) {
          on(searchForm, 'reset', () => {
            setSearchResultsOpen(false);
            if (searchClear) {
              searchClear.hidden = true;
            }
            if (searchInput) {
              window.requestAnimationFrame(() => searchInput.focus());
            }
          });
        }

        if (searchClose) {
          on(searchClose, 'click', (event) => {
            event.preventDefault();
            if (searchInput) {
              searchInput.value = '';
            }
            if (searchClear) {
              searchClear.hidden = true;
            }
            setSearchResultsOpen(false);
            setSearchOpen(false, { focusInput: false });
          });
        }
      }

      on(interactionBackdrop, 'click', (event) => {
        event.preventDefault();
        setSearchOpen(false, { focusInput: false });
        closeAll();
        setOverlayInteraction(false);
      });

      on(nav, 'mouseleave', () => {
        if (!isHoverMode()) return;
        scheduleCloseAll();
      });

      on(nav, 'mouseenter', () => {
        window.clearTimeout(closeTimer);
      });

      on(sectionRoot, 'mouseenter', () => {
        if (!isHoverMode()) return;
        setOverlayInteraction(true);
      });

      on(sectionRoot, 'mouseleave', () => {
        if (!isHoverMode()) return;
        setOverlayInteraction(false);
      });

      on(sectionRoot, 'focusin', () => {
        setOverlayInteraction(true);
      });

      on(sectionRoot, 'focusout', (event) => {
        if (sectionRoot.contains(event.relatedTarget)) return;
        setOverlayInteraction(false);
      });

      on(window, 'resize', () => {
        refreshStickyHeightCache();
        scheduleStickyState();
        syncStickyVisibleOffset();
        if (!isHoverMode()) return;
        if (!activeItem || !activeItem.hasAttribute('open') || !isMegaItem(activeItem)) return;
        syncMegaLayer(activeItem);
      }, { passive: true });

      on(window, 'scroll', () => {
        scheduleStickyState();

        const keepMenuOpenOnScroll = Boolean(
          isHoverMode()
          && activeItem
          && activeItem.hasAttribute('open')
          && (
            sectionRoot.matches(':hover')
            || (nav && nav.matches(':hover'))
            || activeItem.matches(':hover')
          )
        );

        const activeElement = document.activeElement;
        const searchHasFocus = Boolean(
          searchOpen
          && activeElement
          && (
            (searchPanel && searchPanel.contains(activeElement))
            || (searchResults && searchResults.contains(activeElement))
          )
        );

        const keepSearchOpenOnScroll = Boolean(
          isHoverMode()
          && searchOpen
          && (
            sectionRoot.matches(':hover')
            || (searchPanel && searchPanel.matches(':hover'))
            || (searchResults && searchResults.matches(':hover'))
            || searchHasFocus
          )
        );

        if (keepMenuOpenOnScroll) {
          if (stickyMode === 'scroll_up') {
            syncStickyClass('header--sticky-hidden', false);
          }
          syncMegaLayer(activeItem);
          updateOverlayState();
          return;
        }

        if (keepSearchOpenOnScroll) {
          if (stickyMode === 'scroll_up') {
            syncStickyClass('header--sticky-hidden', false);
          }
          updateOverlayState();
          return;
        }

        if (!activeItem && !searchOpen) return;
        setSearchOpen(false, { focusInput: false });
        closeAll();
      }, { passive: true });

      on(document, 'click', (event) => {
        if (sectionRoot.contains(event.target)) return;
        setSearchOpen(false, { focusInput: false });
        closeAll();
      });

      on(document, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (searchOpen) {
          setSearchOpen(false, { focusInput: false });
          if (searchToggle) searchToggle.focus();
          return;
        }
        const activeTrigger = activeItem ? getTrigger(activeItem) : null;
        closeAll();
        if (activeTrigger) activeTrigger.focus();
      });

      const syncStickyWithDrawerState = (event) => {
        const hasExplicitOpenState = typeof event?.detail?.open === 'boolean';

        if (hasExplicitOpenState) {
          drawerStateHintOpen = event.detail.open;
        } else if (event?.type === 'theme:cart:open') {
          drawerStateHintOpen = true;
        } else {
          drawerStateHintOpen = hasActiveDrawer();
        }

        if (stickyMode === 'scroll_up' && isDrawerOpenForSticky()) {
          syncStickyClass('header--sticky-hidden', false);
        }

        if (hasExplicitOpenState && event.detail.open === false) {
          window.requestAnimationFrame(() => {
            drawerStateHintOpen = hasActiveDrawer();
            scheduleStickyState();
          });
          return;
        }

        scheduleStickyState();
      };

      on(document, 'theme:drawer-menu:state', syncStickyWithDrawerState);
      on(document, 'theme:localization-drawer:state', syncStickyWithDrawerState);
      on(document, 'theme:cart-drawer:state', syncStickyWithDrawerState);
      on(document, 'theme:search-drawer:state', syncStickyWithDrawerState);
      on(document, 'theme:cart:open', syncStickyWithDrawerState);
      on(document, 'theme:cart:close', syncStickyWithDrawerState);

      const handleInteractionModeChange = () => {
        setOverlayInteraction(false);
        setSearchOpen(false, { focusInput: false });
        closeAll();
        scheduleStickyState();
        updateOverlayState();
        syncHeaderInteractionScrollLock();
      };

      addMediaListener(hoverMedia, handleInteractionModeChange, disposers);
      addMediaListener(widthMedia, handleInteractionModeChange, disposers);

      syncAllExpanded();
      syncSearchState();
      syncMegaLayer(activeItem);
      syncStickyModeClass();
      applyStickyState();
      syncStickyVisibleOffset();
      updateOverlayState();
      syncHeaderInteractionScrollLock();
      window.requestAnimationFrame(() => {
        sectionRoot.classList.remove('header--loading');
      });

      return () => {
        clearTimers();
        setSearchOpen(false, { focusInput: false });
        closeAll();
        unlockHeaderInteractionScroll();
        syncStickyClass('header--sticky-active', false);
        syncStickyClass('header--sticky-hidden', false);
        setStickyVisibleOffset(0);
        setHeaderSafeOffset(0);
        setHeaderLockTopOffset(0);
        if (document.body) {
          document.body.classList.remove(
            'theme-header-sticky-active',
            'theme-header-sticky-visible',
            'theme-header-overlay-visible',
            'theme-header-safe-offset-active'
          );
        }
        if (sectionShell) {
          stickyModeClasses.forEach((className) => sectionShell.classList.remove(className));
        }
        while (disposers.length) {
          const dispose = disposers.pop();
          if (typeof dispose === 'function') dispose();
        }
      };
    };

    const mountSection = (sectionRoot) => {
      if (!sectionRoot) return;
      const key = getSectionKey(sectionRoot);
      if (!key) return;

      const existing = controllers.get(key);
      if (existing && typeof existing.destroy === 'function') {
        existing.destroy();
      }

      const destroy = initHeaderNav(sectionRoot);
      controllers.set(key, { destroy, root: sectionRoot });
    };

    const unmountSection = (sectionRoot) => {
      if (!sectionRoot) return;
      const key = getSectionKey(sectionRoot);
      if (!key || !controllers.has(key)) return;

      const controller = controllers.get(key);
      if (controller && typeof controller.destroy === 'function') {
        controller.destroy();
      }
      controllers.delete(key);
    };

    document.querySelectorAll(sectionSelector).forEach((sectionRoot) => {
      mountSection(sectionRoot);
    });

    document.addEventListener('shopify:section:load', (event) => {
      const sectionRoot = getSectionRootFromNode(event.target);
      if (sectionRoot) {
        mountSection(sectionRoot);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const sectionRoot = getSectionRootFromNode(event.target);
      if (sectionRoot) {
        unmountSection(sectionRoot);
      }
    });

    window.ThemeHeaderNav = {
      initialized: true,
      initHeaderNav,
      mountSection,
      unmountSection
    };
  })();



/* --- Smooth wheel scroll (desktop only, Lenis-powered) --- */

(() => {
  'use strict';

  if (window.__themeSmoothScrollInitialized) return;
  window.__themeSmoothScrollInitialized = true;

  const SCROLL_LOCK_CLASS = 'theme-scroll-lock-active';
  const POINTER_FINE_QUERY = '(pointer: fine)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  let enabled = false;
  let lenis = null;
  let pointerFineMedia = null;
  let reducedMotionMedia = null;
  let lockStateObserver = null;
  let originalInlineScrollBehavior = '';
  let forcedScrollBehavior = false;
  let resizeFrame = 0;
  let lastLenisShouldRun = null;
  let rafId = 0;

  const isScrollLocked = () =>
    document.documentElement.classList.contains(SCROLL_LOCK_CLASS);

  const addMediaListener = (mq, cb) => {
    if (!mq) return;
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', cb);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(cb);
    }
  };

  const setScrollBehaviorOverride = (shouldForce) => {
    const root = document.documentElement;
    if (!root) return;

    if (shouldForce) {
      if (forcedScrollBehavior) return;
      originalInlineScrollBehavior = root.style.scrollBehavior || '';
      root.style.scrollBehavior = 'auto';
      forcedScrollBehavior = true;
      return;
    }

    if (!forcedScrollBehavior) return;
    root.style.scrollBehavior = originalInlineScrollBehavior;
    forcedScrollBehavior = false;
  };

  const scheduleLenisResize = () => {
    if (resizeFrame || !lenis) return;

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = 0;
      lenis?.resize();
    });
  };

  const createLenis = () => {
    if (typeof window.Lenis !== 'function') return null;

    // autoRaf is disabled so we can fully suspend the loop when the page is
    // hidden, scroll is locked, or no smoothing is needed — the previous
    // setting kept rAF alive even while .stop() was active.
    // `prevent` is intentionally not used because false positives on complex
    // interactive sections (like image comparison) can interrupt smoothing.
    return new window.Lenis({
      autoRaf: false,
      autoToggle: false,
      anchors: true,
      allowNestedScroll: true,
      naiveDimensions: true,
      stopInertiaOnNavigate: true,
    });
  };

  const stopRaf = () => {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };

  const rafTick = (time) => {
    if (!lenis || lastLenisShouldRun !== true) {
      rafId = 0;
      return;
    }
    lenis.raf(time);
    rafId = window.requestAnimationFrame(rafTick);
  };

  const startRaf = () => {
    if (rafId || !lenis) return;
    rafId = window.requestAnimationFrame(rafTick);
  };

  const destroyLenis = () => {
    stopRaf();
    if (resizeFrame) {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = 0;
    }

    if (!lenis) return;
    lenis.destroy();
    lenis = null;
    lastLenisShouldRun = null;
  };

  const syncLenisLockState = ({ forceResize = false } = {}) => {
    if (!enabled || !lenis) return;

    const shouldRun = !isScrollLocked() && !document.hidden;

    if (!shouldRun) {
      if (lastLenisShouldRun !== false) {
        lenis.stop();
        stopRaf();
      }

      lastLenisShouldRun = false;
      return;
    }

    if (lastLenisShouldRun !== true) {
      lenis.start();
      lastLenisShouldRun = true;
      startRaf();
      scheduleLenisResize();
      return;
    }

    if (forceResize) {
      scheduleLenisResize();
    }

    lastLenisShouldRun = true;
    startRaf();
  };

  const startLockStateObserver = () => {
    if (lockStateObserver) return;
    lockStateObserver = new MutationObserver(syncLenisLockState);
    lockStateObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme-scroll-lock-count']
    });
  };

  const stopLockStateObserver = () => {
    lockStateObserver?.disconnect();
    lockStateObserver = null;
  };

  const shouldEnable = () => {
    if (!pointerFineMedia || !pointerFineMedia.matches) return false;
    if (reducedMotionMedia && reducedMotionMedia.matches) return false;
    if (document.body && document.body.dataset.smoothScroll === 'false') return false;
    if (typeof window.Lenis !== 'function') return false;
    return true;
  };

  const updateState = () => {
    const next = shouldEnable();

    if (next && !enabled) {
      lenis = createLenis();
      if (!lenis) return;

      enabled = true;
      lastLenisShouldRun = null;
      setScrollBehaviorOverride(true);
      startLockStateObserver();
      syncLenisLockState({ forceResize: true });
      return;
    }

    if (!next && enabled) {
      enabled = false;
      stopLockStateObserver();
      destroyLenis();
      setScrollBehaviorOverride(false);
      return;
    }

    if (next && enabled) {
      syncLenisLockState();
    }
  };

  const init = () => {
    pointerFineMedia = window.matchMedia(POINTER_FINE_QUERY);
    reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

    addMediaListener(pointerFineMedia, updateState);
    addMediaListener(reducedMotionMedia, updateState);

    document.addEventListener('visibilitychange', syncLenisLockState);
    window.addEventListener('pageshow', () => syncLenisLockState({ forceResize: true }), { passive: true });

    // The Lenis script is injected lazily on eligible environments (see
    // layout/theme.liquid). When it finishes loading after this script has
    // already initialised, re-evaluate so Lenis can attach without a reload.
    document.addEventListener('theme:lenis-loaded', updateState, { once: true });

    updateState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
